import { apiFetch, buildQuery } from "./client";
import type { Page } from "./admin";
import type { LocationKind, LocationKindBehavior } from "./locations";
import type { AttributeDefView, AssetTypeView } from "./catalog";
import type { PositionPathEntry, PositionView } from "./structure";

/**
 * Sprint 3 contract: docs/api/sprint3-asseti.md §2, §3, §5; sprint 5c (manufacturer on the
 * asset, not the type): docs/api/sprint5c-tip-dela-samo-atributi.md §3.
 */

export const ASSET_CONDITIONS = ["ISPRAVAN", "NEISPRAVAN", "NA_SERVISU", "OTPISAN"] as const;
export type AssetCondition = (typeof ASSET_CONDITIONS)[number];

/** Serbian labels; rendered via t("assets.condition.<CODE>", fallback). */
export const ASSET_CONDITION_LABEL: Record<AssetCondition, string> = {
  ISPRAVAN: "Ispravan",
  NEISPRAVAN: "Neispravan",
  NA_SERVISU: "Na servisu",
  OTPISAN: "Otpisan",
};

export const ASSET_USAGES = ["MONTIRAN", "NA_LAGERU"] as const;
/** Derived server-side: `positionId != null`. */
export type AssetUsage = (typeof ASSET_USAGES)[number];

export const ASSET_USAGE_LABEL: Record<AssetUsage, string> = {
  MONTIRAN: "Montiran",
  NA_LAGERU: "Na lageru",
};

export const REPLACEMENT_KINDS = ["PERMANENT", "TEMPORARY"] as const;
export type ReplacementKind = (typeof REPLACEMENT_KINDS)[number];

export const REPLACEMENT_KIND_LABEL: Record<ReplacementKind, string> = {
  PERMANENT: "Trajna",
  TEMPORARY: "Privremena",
};

export const EVENT_TYPES = ["MOUNT", "DISMOUNT", "MOVE", "CONDITION_CHANGE", "NOTE"] as const;
export type AssetEventType = (typeof EVENT_TYPES)[number];

export const EVENT_TYPE_LABEL: Record<AssetEventType, string> = {
  MOUNT: "Montaža",
  DISMOUNT: "Demontaža",
  MOVE: "Premeštanje",
  CONDITION_CHANGE: "Promena stanja",
  NOTE: "Zapis",
};

/**
 * NONE = structural node (no expected type, nothing mounted);
 * EMPTY = something is expected, nothing mounted; MOUNTED = permanent;
 * TEMPORARY = temporary replacement.
 */
export type MountState = "NONE" | "EMPTY" | "MOUNTED" | "TEMPORARY";

export type LocationRef = {
  id: string; code: string; name: string; kind: LocationKind; kindName: string; kindBehavior: LocationKindBehavior;
  /** Effective GPS of the location (own or nearest ancestor's); null when unknown. */
  lat: number | null; lng: number | null;
};
export type PathEntry = PositionPathEntry;

export type AssetSummary = {
  id: string;
  tag: string;
  /** System-generated ID (A-00000001), never edited. */
  inventoryNo: string;
  serialNo: string | null;
  typeId: string;
  typeCode: string;
  typeName: string;
  /** Sprint 5c / 5g: the concrete piece carries its maker (a row of the manufacturer codebook) and the maker's designation. */
  manufacturerId: string | null;
  /** Name of `manufacturerId` (read-only). */
  manufacturer: string | null;
  manufacturerCode: string | null;
  /** Sprint 5h: who the piece was bought from (a row of the supplier codebook). */
  supplierId: string | null;
  /** Name of `supplierId` (read-only). */
  supplier: string | null;
  /** Made in the workshop. */
  isCustomMade: boolean;
  condition: AssetCondition;
  usage: AssetUsage;
  replacementKind: ReplacementKind | null;
  /** ISO date. */
  replacementReminderAt: string | null;
  positionId: string | null;
  positionName: string | null;
  /** [] when not mounted. */
  positionPath: PathEntry[];
  /** Mounted: effective location of the position; otherwise the asset's own. */
  effectiveLocation: LocationRef | null;
  locationNote: string | null;
  /** Attribute values keyed by attribute key (§2, 2026-09-22) — what the "Svi atributi" table shows; absent on mount candidates. */
  actualValues?: Record<string, string | number | boolean | null>;
  /** ISO date. */
  expectedReturnAt: string | null;
};

export type AssetDetail = AssetSummary & {
  /** Own location (only when not mounted). */
  locationId: string | null;
  actualValues: Record<string, string | number | boolean | null>;
  /** Attribute definitions of the type. */
  attributeDefs: AttributeDefView[];
  note: string | null;
  /** Mounted on a position whose expected type does not match. */
  expectedTypeMismatch: boolean;
  createdAt: string;
  updatedAt: string;
};

/** POST: an asset is always created NOT mounted, on a physical location. */
export type AssetRequest = {
  tag: string;
  serialNo?: string | null;
  typeId: string;
  /** Row of the manufacturer codebook (400 VALIDATION `details.manufacturerId` when unknown or on a custom-made piece). */
  manufacturerId?: string | null;
  manufacturerCode?: string | null;
  /** Row of the supplier codebook (400 VALIDATION `details.supplierId` when unknown or on a custom-made piece). */
  supplierId?: string | null;
  isCustomMade?: boolean;
  /** Default ISPRAVAN; OTPISAN allowed. */
  condition?: AssetCondition;
  locationId: string;
  locationNote?: string | null;
  expectedReturnAt?: string | null;
  actualValues?: Record<string, unknown>;
  note?: string | null;
};

/** PUT: no condition / location / position (those are events). */
export type AssetUpdateRequest = {
  tag: string;
  serialNo?: string | null;
  /** Changeable only while not mounted (409 MOUNTED). */
  typeId: string;
  /** Row of the manufacturer codebook (400 VALIDATION `details.manufacturerId` when unknown or on a custom-made piece). */
  manufacturerId?: string | null;
  manufacturerCode?: string | null;
  /** Row of the supplier codebook (400 VALIDATION `details.supplierId` when unknown or on a custom-made piece). */
  supplierId?: string | null;
  isCustomMade?: boolean;
  actualValues?: Record<string, unknown>;
  note?: string | null;
};

export type MountRequest = {
  positionId: string;
  replacementKind: ReplacementKind;
  replacementReminderAt?: string | null;
  occurredAt?: string | null;
  note?: string | null;
  /** Bypass 409 TYPE_MISMATCH. */
  force?: boolean;
};

export type DismountRequest = {
  toLocationId: string;
  locationNote?: string | null;
  newCondition?: AssetCondition | null;
  /** Required when the destination kind has behavior EXTERNAL_SERVICE. */
  expectedReturnAt?: string | null;
  occurredAt?: string | null;
  note?: string | null;
};

export type AssetMoveRequest = {
  toLocationId: string;
  locationNote?: string | null;
  expectedReturnAt?: string | null;
  occurredAt?: string | null;
  note?: string | null;
};

export type ConditionRequest = { condition: AssetCondition; occurredAt?: string | null; note?: string | null };
export type NoteEventRequest = { note: string; occurredAt?: string | null };
/** For a position: every mounted asset in the sub-tree. */
export type DismountAllRequest = DismountRequest;

export type AssetEventView = {
  id: string;
  assetId: string;
  assetTag: string;
  assetTypeName: string;
  eventType: AssetEventType;
  occurredAt: string;
  performedById: string;
  performedByName: string;
  positionId: string | null;
  positionName: string | null;
  positionPath: PathEntry[];
  fromLocation: LocationRef | null;
  toLocation: LocationRef | null;
  replacementKind: ReplacementKind | null;
  conditionBefore: AssetCondition | null;
  conditionAfter: AssetCondition | null;
  batchId: string | null;
  note: string | null;
  createdAt: string;
};

/** ANY is returned when the position expects no type or when `q` is given. */
export type MatchLevel = "TYPE" | "ANY";
export const MATCH_LEVEL_LABEL: Record<MatchLevel, string> = {
  TYPE: "Tačan tip",
  ANY: "Drugo",
};
export type MountCandidate = AssetSummary & { matchLevel: MatchLevel };

export type DismountAllResult = { batchId: string; count: number; events: AssetEventView[] };

export type AvailabilityGroup = {
  location: LocationRef | null;
  locationPath: PathEntry[];
  condition: AssetCondition;
  usage: AssetUsage;
  count: number;
  assets: AssetSummary[];
};

export type TypeAvailability = {
  typeId: string;
  typeCode: string;
  typeName: string;
  total: number;
  byCondition: Record<AssetCondition, number>;
  mounted: number;
  /** ISPRAVAN & NA_LAGERU. */
  inStock: number;
  /** Sorted: ISPRAVAN+NA_LAGERU, ISPRAVAN+MONTIRAN, NEISPRAVAN, NA_SERVISU, OTPISAN; then by location. */
  groups: AvailabilityGroup[];
};

export type SearchResult = {
  assets: AssetSummary[];
  positions: PositionView[];
  types: AssetTypeView[];
};

export type AssetListParams = {
  /** ILIKE over tag / serialNo / manufacturer / manufacturerCode / type code / type name. */
  q?: string;
  typeId?: string;
  condition?: AssetCondition;
  usage?: AssetUsage;
  /** Effective location within that location's sub-tree. */
  locationId?: string;
  positionId?: string;
  /** TEMPORARY with replacementReminderAt <= today. */
  reminderDue?: boolean;
  page?: number;
  size?: number;
  sort?: string;
};

export type PageParams = { page?: number; size?: number };

// ---------------------------------------------------------------------------
// Advanced search (sprint 5i)
// ---------------------------------------------------------------------------

/**
 * One criterion. `field` is a column (`typeId`, `condition`, `usage`, `manufacturerId`,
 * `supplierId`, `customMade`, `replacementKind`, `tag`, `inventoryNo`, `serialNo`,
 * `manufacturerCode`, `replacementReminderAt`, `expectedReturnAt`), a tree (`positionId`,
 * `locationId` — the node and everything below) or an attribute (`attr:<key>`). Which part is
 * read depends on the field: `values` (any of), `text` (contains), `from` / `to` (number or
 * ISO date, either end optional). `empty` asks for assets without a value in the field.
 */
export type AssetFilter = {
  field: string;
  values?: string[];
  text?: string;
  from?: string;
  to?: string;
  empty?: boolean;
};

export type AssetSearchRequest = {
  filters: AssetFilter[];
  page?: number;
  size?: number;
  /** `"<field>,<asc|desc>"`: tag, inventoryNo, serialNo, type, manufacturer, supplier, condition, attr:<key>. */
  sort?: string;
};

export type AssetFacetOption = { value: string; label: string | null; count: number };

/** Choices of one field, counted over the assets matching every other criterion. */
export type AssetFacet = { options: AssetFacetOption[]; min: string | null; max: string | null; emptyCount: number };

export type AssetSearchFacets = {
  total: number;
  /** Attribute key → matching assets that carry a value for it. */
  attributeCounts: Record<string, number>;
  facets: Record<string, AssetFacet>;
};

export const assetsApi = {
  list: (params: AssetListParams = {}) =>
    apiFetch<Page<AssetSummary>>(`/assets${buildQuery(params)}`),
  /** Advanced search (sprint 5i): every criterion must hold; 400 VALIDATION on a bad one. */
  advancedSearch: (body: AssetSearchRequest, signal?: AbortSignal) =>
    apiFetch<Page<AssetSummary>>("/assets/search", { method: "POST", body, signal }),
  /** What the search can still be narrowed by: match count, attributes left, choices per field. */
  advancedFacets: (body: { filters: AssetFilter[]; fields: string[] }, signal?: AbortSignal) =>
    apiFetch<AssetSearchFacets>("/assets/search/facets", { method: "POST", body, signal }),
  get: (id: string) => apiFetch<AssetDetail>(`/assets/${id}`),
  /** QR scan entry point. */
  /** Scanned / typed code: system id (A-00000053) or tag; a label URL is accepted too (§12). */
  byTag: (code: string) => apiFetch<AssetDetail>(`/assets/by-tag/${encodeURIComponent(code)}`),
  /** 201; 409 TAG_TAKEN. */
  create: (body: AssetRequest) => apiFetch<AssetDetail>("/assets", { method: "POST", body }),
  /** 409 MOUNTED when typeId changes while mounted. */
  update: (id: string, body: AssetUpdateRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}`, { method: "PUT", body }),
  /** 204; 409 MOUNTED. */
  /** `reason` is mandatory (2026-09-22). */
  remove: (id: string, reason: string) => apiFetch<void>(`/assets/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
  /** 409 NOT_OPERATIONAL / ALREADY_MOUNTED / POSITION_OCCUPIED / TYPE_MISMATCH. */
  mount: (id: string, body: MountRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}/mount`, { method: "POST", body }),
  /** Mounted asset straight onto another position (DISMOUNT + MOUNT, one batch); 409 NOT_MOUNTED, same 409s as mount. */
  remount: (id: string, body: MountRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}/remount`, { method: "POST", body }),
  /** 409 NOT_MOUNTED; 400 VALIDATION without toLocationId. */
  dismount: (id: string, body: DismountRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}/dismount`, { method: "POST", body }),
  /** 409 MOUNTED. */
  move: (id: string, body: AssetMoveRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}/move`, { method: "POST", body }),
  /** OTPISAN while mounted → 409 MOUNTED; same condition → 400 VALIDATION. */
  condition: (id: string, body: ConditionRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}/condition`, { method: "POST", body }),
  note: (id: string, body: NoteEventRequest) =>
    apiFetch<AssetDetail>(`/assets/${id}/note`, { method: "POST", body }),
  /** Sorted occurredAt DESC. */
  events: (id: string, params: PageParams = {}) =>
    apiFetch<Page<AssetEventView>>(`/assets/${id}/events${buildQuery(params)}`),
  /** Part search / alternative suggestion. */
  availability: (typeId: string) => apiFetch<TypeAvailability>(`/asset-types/${typeId}/availability`),
  /** Global topbar search: 10 best per group. */
  search: (q: string, signal?: AbortSignal) =>
    apiFetch<SearchResult>(`/search${buildQuery({ q })}`, { signal }),
};

// ---------------------------------------------------------------------------
// Manufacturer codebook ("Proizvođači", sprint 5g: docs/api/sprint5g-sifarnik-proizvodjaca.md)
// ---------------------------------------------------------------------------

export type ManufacturerView = {
  id: string;
  name: string;
  /** Free note about the maker (contact, service…). */
  note: string | null;
  /** Active assets made by it. */
  assetCount: number;
  createdAt: string;
  updatedAt: string;
};

export type ManufacturerRequest = { name: string; note?: string | null };

export const manufacturersApi = {
  /** The whole codebook, sorted by name; readable by everyone signed in. */
  list: () => apiFetch<ManufacturerView[]>("/manufacturers"),
  /** 201; 409 NAME_TAKEN (letter case ignored). */
  create: (body: ManufacturerRequest) => apiFetch<ManufacturerView>("/manufacturers", { method: "POST", body }),
  /** Name and note (the note is replaced as sent); a rename shows on every asset at once. */
  update: (id: string, body: ManufacturerRequest) =>
    apiFetch<ManufacturerView>(`/manufacturers/${id}`, { method: "PUT", body }),
  /** 409 IN_USE (`details.assetCount`) while an active asset points at it. */
  remove: (id: string) => apiFetch<void>(`/manufacturers/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Supplier codebook ("Dobavljači", sprint 5h: docs/api/sprint5h-sifarnik-dobavljaca.md)
// ---------------------------------------------------------------------------

export type SupplierView = {
  id: string;
  name: string;
  /** PIB — 9 digits; unique among suppliers when given. */
  taxId: string | null;
  /** Matični broj — 8 digits. */
  registrationNo: string | null;
  address: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  contactPerson: string | null;
  note: string | null;
  /** Active assets bought from it. */
  assetCount: number;
  createdAt: string;
  updatedAt: string;
};

/** Only `name` is mandatory; every other field is replaced as sent, null / blank clears it. */
export type SupplierRequest = {
  name: string;
  taxId?: string | null;
  registrationNo?: string | null;
  address?: string | null;
  city?: string | null;
  phone?: string | null;
  email?: string | null;
  contactPerson?: string | null;
  note?: string | null;
};

export const suppliersApi = {
  /** The whole codebook, sorted by name; readable by everyone signed in. */
  list: () => apiFetch<SupplierView[]>("/suppliers"),
  get: (id: string) => apiFetch<SupplierView>(`/suppliers/${id}`),
  /** 201; 409 NAME_TAKEN (letter case ignored) / TAX_ID_TAKEN (`details.supplierName`); 400 VALIDATION. */
  create: (body: SupplierRequest) => apiFetch<SupplierView>("/suppliers", { method: "POST", body }),
  /** Full replace of the company data; a rename shows on every asset at once. */
  update: (id: string, body: SupplierRequest) => apiFetch<SupplierView>(`/suppliers/${id}`, { method: "PUT", body }),
  /** 409 IN_USE (`details.assetCount`) while an active asset points at it. */
  remove: (id: string) => apiFetch<void>(`/suppliers/${id}`, { method: "DELETE" }),
};
